// FilmDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate,Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import data from '../data/movies.json';
import screeningsData from '../data/screenings.json';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReviewForm from './ReviewForm';
import ReviewCard from "./ReviewCard";
import ReviewStats from './ReviewStats';
import '../styles/FilmDetail.css';
import { slugify } from './utils';


function convertDuration(durationStr) {

    // Si l'entrée est vide ou non définie, retourne une chaîne vide
    if (!durationStr || durationStr.trim() === '') {
        return '';
    }

    // Extraire le nombre de minutes à partir de la chaîne (en supposant le format "X min")
    const minutes = parseInt(durationStr, 10);

    // Si la durée est supérieure ou égale à 60 minutes
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes ? `${hours}h ${remainingMinutes} min` : `${hours}h`;
    }
    // Si la durée est inférieure à 60 minutes
    return `${minutes} min`;
}

const FilmDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { slug } = useParams();
    const film = data.find(film => slugify(film.titre) === slug);

    // États locaux pour screenings, avis et affichage progressif
    const [screenings, setScreenings] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [visibleCount, setVisibleCount] = useState(5);


    // Filtrer les séances futures pour le film en question

    useEffect(() => {
        const upcomingScreenings = screeningsData
            .filter(s => s.filmId === film.id)
            .filter(s => new Date(s.heure_de_sortie) > new Date());
        setScreenings(upcomingScreenings);
    }, [id]);

    // Récupérer les avis depuis le backend (API)
    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const response = await fetch(`https://api-4.africadataentry.net/api/ratings/${film.id}`);
                //const response = await fetch(process.env.REACT_APP_API_URL_LIGN+`/${film.id}`);
                if (!response.ok) throw new Error('Erreur lors de la récupération des avis');
                const data = await response.json();
                // Assurez-vous que data.reviews existe et est un tableau
                setReviews(data.reviews || []); 
            } catch (error) {
                console.error(error);
                setReviews([]); // En cas d'erreur, définir un tableau vide
            }
        };
        fetchReviews();
    }, [id]);

    // Ajout d'un nouvel avis dans l'état local
    const handleReviewSubmit = (reviewData) => {
        setReviews([reviewData, ...reviews]);
        // Afficher une notification
    toast.success("Votre avis a bien été enregistré !", {
        position: "top-right",
        autoClose: 3000, // Disparition après 3 secondes
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
    });
    };

    if (!film) return <div>Film non trouvé</div>;

    // Trier les avis du plus récent au plus ancien et sélectionner ceux à afficher
    const sortedReviews = [...reviews].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    const visibleReviews = sortedReviews.slice(0, visibleCount);

    return (
        <div className="film-detail">
            {/* Section principale : Affiche + Infos */}
            <div className="film-grid">
                <div className="film-poster">
                    <motion.img
                        src={film.poster}
                        alt={film.titre}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    />
                </div>
                <div className="film-main-info">
                    <h1 className="film-title">{film.titre}</h1>
                    <div className="metadata">
                        <p className="info-item"><strong>Type :</strong> {film.type}</p>
                        <p className="info-item"><strong>Réalisateur :</strong> {film.realisateur}</p>
                        <p className="info-item"><strong>Année :</strong> {film.annee}</p>
                        <p className="info-item"><strong>Genre :</strong> {film.genre.join(', ')}</p>
                        <p className="info-item"><strong>Durée :</strong> {convertDuration(film.runtime)}</p>
                    </div>

                    {/* Section Screenings */}
                    {screenings.length > 0 && (
                        <div className="screenings-section">
                            <h2 className="section-title">Prochaines séances</h2>
                            <div className="screenings-grid">
                                {screenings.map((screening, index) => {
                                    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(screening.adresse)}`;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="screening-card"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                        >
                                            <div className="screening-header">
                                                <span className="calendar-icon">📅</span>
                                                <h3>{screening.cinema}</h3>
                                            </div>
                                            <p className="screening-address">
                                                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                                                    📍 {screening.adresse} <span className="maps-label">(Voir sur Maps)</span>
                                                </a>
                                            </p>
                                            <p className="screening-time">
                                                {new Date(screening.heure_de_sortie).toLocaleDateString('fr-FR', {
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    year: 'numeric'
                                                })}
                                            </p>
                                            <p className="screening-ticket">
                                                    🎟️ <span className="maps-label">Prix d'un ticket {screening.price} FCFA</span>
                                            </p>
                                            {/*
                                            <Link to={`/pages/ticket/${film.id}`}>
                                            <p className="screening-ticket">
                                                    🎟️ <span className="maps-label">Prix d'un ticket({screening.price})</span>
                                            </p>
                                            </Link>*/}

                                        </motion.div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Synopsis & Description */}
            <div className="synopsis-description">
                <h2 className="section-title">Synopsis</h2>
                <p className="synopsis-text">{film.synopsis}</p>
                <h2 className="section-title">Description</h2>
                <p className="description-text">{film.description}</p>
            </div>

            {/* Bande-Annonce */}
            <div className="film-summary">
                <h2>Bande annonce</h2>
                <div className="video-container">
                    <iframe
                        width="560"
                        height="315"
                        src={film.bande_annonce}
                        title="Bande annonce"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            </div>
            <h2>Avis des utilisateurs</h2>
            {/* Statistiques des avis */}
            <ReviewStats reviews={reviews} />

            {/* Liste des avis */}
            <div className="reviews-list">
                {visibleReviews.map((review, index) => (
                    <ReviewCard key={review._id || index} review={review} />
                ))}
            </div>

            {/* Bouton "Voir les autres avis" */}
            {visibleCount < sortedReviews.length && (
                <button className="load-more-button" onClick={() => setVisibleCount(visibleCount + 5)}>
                    Voir les autres avis
                </button>
            )}

            {/* Formulaire d'ajout d'avis */}
            <div className="user-interaction">
                <div className="reviews-section">

                    <ReviewForm filmId={film.id} onSubmit={handleReviewSubmit} />
                </div>
            </div>
            <ToastContainer />
        </div>
        
    );
};

export default FilmDetail;
